import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, InlineStack, TextField, DropZone, Text, Button, Icon, Spinner } from '@shopify/polaris';
import { MagicMinor, UploadMajor, ImportMinor } from '@shopify/polaris-icons';
import { BlobServiceClient } from '../services/blobStorageCompat';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';

const AIDesignTestPage = ({ embedded = false, setToastMessage, setToastActive }) => {
  const [faceUrl, setFaceUrl] = useState('');
  const [maskUrl, setMaskUrl] = useState('');

  const [faceFiles, setFaceFiles] = useState([]);
  const [maskFiles, setMaskFiles] = useState([]);

  const [azureSettings, setAzureSettings] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');

  const [faceAzureUrl, setFaceAzureUrl] = useState('');
  const [maskAzureUrl, setMaskAzureUrl] = useState('');

  const [faceFileKey, setFaceFileKey] = useState('');
  const [maskFileKey, setMaskFileKey] = useState('');

  const previewContainerRef = useRef(null);

  const loadAzureSettings = useCallback(async () => {
    try {
      const baseUrl = getServerURL();
      const resp = await fetch(`${baseUrl}/settings/storage/client`, {
        headers: authService.getAuthHeaders(),
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      if (json?.success && json?.data && json.data.AZURE_BLOB_BASE_URL && json.data.AZURE_SAS_TOKEN) {
        setAzureSettings(json.data);
        return json.data;
      }
      return null;
    } catch (_e) {
      return null;
    }
  }, []);

  useEffect(() => {
    loadAzureSettings();
  }, [loadAzureSettings]);

  const uploadFileToAzure = useCallback(async (file) => {
    if (!azureSettings) throw new Error('Azure settings not loaded');
    const { AZURE_BLOB_BASE_URL, AZURE_SAS_TOKEN } = azureSettings;
    const containerUrl = AZURE_BLOB_BASE_URL + AZURE_SAS_TOKEN;
    const blobServiceClient = new BlobServiceClient(containerUrl);
    const containerClient = blobServiceClient.getContainerClient("");
    const ext = (file.name?.split('.').pop() || 'png').toLowerCase();
    const randomTxt = `${Math.random().toString(36).substring(2, 8)}${Date.now()}`;
    const newFileName = `test_input_${randomTxt}.${ext}`;
    const blockBlobClient = containerClient.getBlockBlobClient(newFileName);
    const { publicUrl } = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type || 'image/png' },
    });
    return publicUrl;
  }, [azureSettings]);

  const ensureAzure = useCallback(async () => {
    if (!azureSettings) {
      const settings = await loadAzureSettings();
      if (!settings) throw new Error('Azure settings not available');
    }
  }, [azureSettings, loadAzureSettings]);

  const computeKey = (file) => (file ? `${file.name}|${file.size}|${file.lastModified}` : '');

  const handleDropFace = useCallback((droppedFiles, acceptedFiles) => {
    const files = acceptedFiles || droppedFiles || [];
    setFaceFiles(files);
    const first = files?.[0];
    const newKey = computeKey(first);
    if (newKey !== faceFileKey) {
      setFaceAzureUrl('');
      setFaceFileKey(newKey);
    }
    if (files.length > 0) setFaceUrl('');
  }, [faceFileKey]);



  const handleDropMask = useCallback((droppedFiles, acceptedFiles) => {
    const files = acceptedFiles || droppedFiles || [];
    setMaskFiles(files);
    const first = files?.[0];
    const newKey = computeKey(first);
    if (newKey !== maskFileKey) {
      setMaskAzureUrl('');
      setMaskFileKey(newKey);
    }
    if (files.length > 0) setMaskUrl('');
  }, [maskFileKey]);

  const faceFirstPreviewUrl = useMemo(() => faceFiles?.[0] ? URL.createObjectURL(faceFiles[0]) : undefined, [faceFiles]);
  const maskFirstPreviewUrl = useMemo(() => maskFiles?.[0] ? URL.createObjectURL(maskFiles[0]) : undefined, [maskFiles]);

  useEffect(() => () => {
    if (faceFirstPreviewUrl) URL.revokeObjectURL(faceFirstPreviewUrl);
    if (maskFirstPreviewUrl) URL.revokeObjectURL(maskFirstPreviewUrl);
  }, [faceFirstPreviewUrl, maskFirstPreviewUrl]);

  const getFinalUrl = useCallback(async (typedUrl, files, cachedUrl, cachedKey, setCachedUrl, setCachedKey) => {
    const direct = (typedUrl || '').trim();
    if (direct) return direct;
    if (!files || files.length === 0) return '';
    const first = files[0];
    const currentKey = computeKey(first);
    if (cachedUrl && currentKey === cachedKey) {
      return cachedUrl;
    }
    await ensureAzure();
    const uploaded = await uploadFileToAzure(first);
    setCachedUrl(uploaded);
    setCachedKey(currentKey);
    return uploaded;
  }, [ensureAzure, uploadFileToAzure]);

  const handleDownloadPreview = useCallback(async () => {
    if (!selectedPreviewUrl) return;
    try {
      const resp = await fetch(selectedPreviewUrl, { mode: 'cors' });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = (selectedPreviewUrl.split('/').pop() || 'image').split('?')[0] || 'image';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_ignore) {}
  }, [selectedPreviewUrl]);

  const notify = useCallback((message) => {
    if (typeof setToastMessage === 'function' && typeof setToastActive === 'function') {
      setToastMessage(message);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 2000);
    }
  }, [setToastMessage, setToastActive]);

  const handleGenerate = useCallback(async () => {
    if (isGenerating) return;
    const baseUrl = getServerURL();
    try {
      setIsGenerating(true);

      const finalFaceUrl = await getFinalUrl(faceUrl, faceFiles, faceAzureUrl, faceFileKey, setFaceAzureUrl, setFaceFileKey);
      const finalMaskUrl = await getFinalUrl(maskUrl, maskFiles, maskAzureUrl, maskFileKey, setMaskAzureUrl, setMaskFileKey);

      if (!finalFaceUrl || !finalMaskUrl) {
        notify('Please provide Face and Mask images');
        setIsGenerating(false);
        return;
      }
  
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minutes timeout

      const resp = await fetch(`${baseUrl}/test-scene`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          kid_image: finalFaceUrl,
          mask: finalMaskUrl,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      let apiOutputUrl = '';
      try {
        const data = await resp.clone().json();
        apiOutputUrl = data?.imageUrl || data?.data?.imageUrl || '';
      } catch (_ignore) {}

      if (!resp.ok && !apiOutputUrl) {
        notify(`Generation failed (${resp.status})`);
        setIsGenerating(false);
        return;
      }

      if (apiOutputUrl) setSelectedPreviewUrl(apiOutputUrl);
    } catch (err) {
      if (err.name === 'AbortError') {
        notify('Request timed out after 3 minutes');
      } else {
        notify('Unexpected error while generating');
      }
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, faceUrl, maskUrl, faceFiles, maskFiles, faceAzureUrl, maskAzureUrl, faceFileKey, maskFileKey, getFinalUrl, notify]);

  const renderInputBlock = (label, urlValue, setUrlValue, files, setFiles, firstPreviewUrl, onDrop) => (
    <>
      <TextField
        label={`${label} URL`}
        value={urlValue}
        onChange={(value) => {
          if (value && files.length > 0) {
            setFiles([]);
          }
          setUrlValue(value);
        }}
        placeholder="https://example.com/image.png"
        autoComplete="off"
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 8px' }}>
        <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
        <Text as="span" tone="subdued">-OR-</Text>
        <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
      </div>

      <DropZone accept="image/*" allowMultiple={false} onDrop={onDrop} variableHeight>
        <div style={{ padding: 16, textAlign: 'center', width: '100%' }}>
          {firstPreviewUrl ? (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <button
                onClick={() => setFiles([])}
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: 18,
                  fontWeight: 'bold',
                  zIndex: 2,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                }}
                aria-label="Remove image"
                title="Remove image"
              >
                ×
              </button>
              <img
                src={firstPreviewUrl}
                alt="Uploaded preview"
                style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8 }}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <Icon source={UploadMajor} tone="base" />
              <Text as="p" variant="bodyMd" tone="subdued">
                Drag and drop an image here, or click to upload
              </Text>
            </div>
          )}
        </div>
      </DropZone>
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <InlineStack gap="400" wrap={false}>
        <Box
          padding="400"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="0"
          minHeight="1024px"
          style={{ width: '33.3333%', maxHeight: embedded ? '70vh' : '100vh', display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto' }}
          className="Polaris-ShadowBevel"
        >
          <div style={{ marginBottom: 12 }}>
            <Text variant="headingMd" as="h2">Test Scene Inputs</Text>
          </div>

          {renderInputBlock('Face Image', faceUrl, setFaceUrl, faceFiles, setFaceFiles, faceFirstPreviewUrl, handleDropFace)}

          {renderInputBlock('Mask Image', maskUrl, setMaskUrl, maskFiles, setMaskFiles, maskFirstPreviewUrl, handleDropMask)}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button icon={MagicMinor} variant="primary" loading={isGenerating} onClick={handleGenerate} style={{ minWidth: 140 }}>
              Generate
            </Button>
          </div>
        </Box>
        <Box
          padding="400"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="0"
          minHeight="1024px"
          style={{ width: '66.6667%', maxHeight: embedded ? '70vh' : '100vh', overflow: 'hidden' }}
          className="Polaris-ShadowBevel"
        >
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text variant="headingMd" as="h2">Preview</Text>
            <InlineStack gap="200" align="center">
              <Button icon={ImportMinor} onClick={handleDownloadPreview} disabled={!selectedPreviewUrl}>
                Download
              </Button>
            </InlineStack>
          </div>
          <div
            ref={previewContainerRef}
            style={{
              width: '100%',
              height: 'calc(100% - 40px)',
              background: '#f5f6f7',
              borderRadius: 12,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {selectedPreviewUrl ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${selectedPreviewUrl})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
            ) : (
              <Text tone="subdued">No image generated yet</Text>
            )}
            {isGenerating && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size="large" />
              </div>
            )}
          </div>
        </Box>
      </InlineStack>
    </div>
  );
};

export default AIDesignTestPage;


